import React from "react";
import PropTypes from "prop-types";
import { Empty, Icon } from "antd";
import SectionBlock from "./components/section-block";
import { SECTION_IDS } from "./constants";

/**
 * Credit Brief — a tight one/two-page case summary for a senior credit reviewer.
 *
 * The brief is composed on the frontend from the data the tab already fetched
 * and rendered to a polished PDF by the backend (weasyprint). This section shows
 * the FINAL RENDERED PDF inline (not a raw-HTML preview): click Generate to
 * render it, then it appears in the embedded viewer with an "open in new tab"
 * link.
 */
// Regenerating the PDF (once one already exists) is only actionable at the
// credit stages — same gate used elsewhere in Credit Checker (TVR reset,
// bank statement rebuild). The first-ever "Generate PDF" is left alone.
const canRegenerate = stageId => [7, 8].includes(Number(stageId));

const CreditBriefSection = ({
    innerRef,
    caseId,
    stageId,
    expanded,
    onToggle,
    onDownloadPdf,
    pdfLoading,
    pdfUrl
}) => {
    const regenerateBlocked = Boolean(pdfUrl) && !canRegenerate(stageId);
    return (
    <SectionBlock
        id={SECTION_IDS.CREDIT_BRIEF}
        innerRef={innerRef}
        title={"Credit Brief"}
        subtitle={"One/two-page rendered PDF for senior credit review"}
        icon={"read"}
        expanded={expanded}
        onToggle={onToggle}
        extra={
            onDownloadPdf ? (
                <button
                    type={"button"}
                    className={"cc-brief-pdf-btn"}
                    onClick={onDownloadPdf}
                    disabled={pdfLoading || regenerateBlocked}
                    title={
                        regenerateBlocked
                            ? "The Credit Brief can only be regenerated at the credit stage"
                            : "Render the Credit Brief PDF"
                    }
                >
                    <Icon type={pdfLoading ? "loading" : "file-pdf"} />
                    {pdfLoading
                        ? "Generating…"
                        : pdfUrl
                            ? "Regenerate PDF"
                            : "Generate PDF"}
                </button>
            ) : null
        }
    >
        {pdfUrl ? (
            <div className={"cc-brief-pdfwrap"}>
                <div className={"cc-brief-pdfbar"}>
                    <span>
                        <Icon type={"file-pdf"} /> Rendered Credit Brief
                    </span>
                    <span>
                        <a
                            href={pdfUrl}
                            download={`Credit Summary - ${caseId}.pdf`}
                            target={"_blank"}
                            rel={"noreferrer"}
                        >
                            <Icon type={"download"} /> Download
                        </a>
                    </span>
                </div>
                <iframe
                    title={"credit-brief-pdf"}
                    className={"cc-brief-pdfview"}
                    src={pdfUrl}
                />
            </div>
        ) : (
            <Empty
                description={
                    pdfLoading
                        ? "Rendering the credit brief…"
                        : "Click “Generate PDF” to render the credit brief"
                }
            />
        )}
    </SectionBlock>
    );
};

CreditBriefSection.propTypes = {
    innerRef: PropTypes.object,
    caseId: PropTypes.number,
    // Gates "Regenerate PDF" (once a PDF already exists) to the credit
    // stages (7, 8).
    stageId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    expanded: PropTypes.bool,
    onToggle: PropTypes.func,
    onDownloadPdf: PropTypes.func,
    pdfLoading: PropTypes.bool,
    pdfUrl: PropTypes.string
};

export default CreditBriefSection;
